import { FilterOption } from "@common/components/filterPopover/FilterPopover.types.ts";
import { filterOperatorsKey } from "@common/components/filterBar/filterOperatorHydration.ts";

// The from/to submenus - date, amount, number and recurrence - always emit a fully keyed object, so a filter the
// user emptied arrives here as { from: undefined, to: undefined } rather than as nothing at all. Judging an object
// by its key count would read that as a value, which both hides the emptied chip and sends a valueless range on to
// the server. A Date has to be answered before the object branch: it carries no own enumerable properties, so
// recursing into it would report an emptiness the picker never produced.
export function hasFilterValue(value: unknown): boolean {
    if (value == null) {
        return false;
    }
    if (typeof value === "string") {
        return value.trim().length > 0;
    }
    if (Array.isArray(value)) {
        return value.length > 0;
    }
    if (typeof value === "boolean") {
        return true;
    }
    if (typeof value === "number") {
        return !isNaN(value);
    }
    if (value instanceof Date) {
        return !isNaN(value.getTime());
    }
    if (typeof value === "object") {
        return Object.values(value as object).some(hasFilterValue);
    }
    return Boolean(value);
}

export function isFilterChipDisplayed(option: FilterOption, pinnedFilterIds: Array<string> | undefined): boolean {
    return hasFilterValue(option.value) || (pinnedFilterIds ?? []).includes(option.id);
}

// The next set of on-screen-but-valueless filters after a change: whatever was on screen before, minus the one the
// X removed, minus whatever now holds a value. Derived from the previous state rather than from which filter the
// user touched, so emptying a chip and emptying it from the filter menu need no separate handling. Returns
// undefined rather than an empty array because the persisted filter blob is compared by key count - an empty
// array would read as a change from the saved view before the user has done anything.
export function nextPinnedFilterIds(
    previousOptions: Array<FilterOption>,
    previousPinnedIds: Array<string> | undefined,
    updatedOptions: Array<FilterOption>,
    removedId?: string
): Array<string> | undefined {
    const previouslyDisplayed = new Set(previousPinnedIds ?? []);
    previousOptions.forEach((option) => {
        if (hasFilterValue(option.value)) {
            previouslyDisplayed.add(option.id);
        }
    });

    const valuedIds = new Set(updatedOptions.filter((option) => hasFilterValue(option.value)).map((option) => option.id));
    const stillEmpty = [...previouslyDisplayed].filter((id) => id !== removedId && !valuedIds.has(id));

    return stillEmpty.length > 0 ? stillEmpty : undefined;
}

export const pinnedFilterIdsKey = "pinnedFilterIds";

// The pins live inside the persisted filter blob so they travel with a saved view. Returns undefined when there is
// no filter data to attach them to, which the caller must read as "leave storage alone". Writing a blob out of
// nothing would be indistinguishable from a real, empty filter and would permanently suppress the fallback to the
// user's default view, which keys off filterData being undefined. Nothing is lost by declining: a pin means a
// filter was emptied, so there is always filter data by the time one exists.
export function nextStoredFilterData(currentFilterData: unknown, ids: Array<string> | undefined): Record<string, unknown> | undefined {
    if (currentFilterData === undefined || currentFilterData === null) {
        return undefined;
    }

    const filterData = { ...(currentFilterData as Record<string, unknown>) };
    delete filterData[pinnedFilterIdsKey];
    if (ids !== undefined && ids.length > 0) {
        filterData[pinnedFilterIdsKey] = ids;
    }

    return filterData;
}

// Pins and operators are presentation state the query layer never reads, so they are stripped before the filter blob
// becomes a react-query key or a request body - otherwise pinning an already-empty filter, or changing its operator,
// refetches the whole list. The operators a query does need are the ones inside each filter's own entry, which the
// backend reads; this map is only how an empty filter remembers its own.
export function withoutFilterPresentationState<T>(filterData: T | undefined): T | undefined {
    if (filterData === undefined || filterData === null) {
        return filterData;
    }

    const source = filterData as Record<string, unknown>;
    if (!(pinnedFilterIdsKey in source) && !(filterOperatorsKey in source)) {
        return filterData;
    }

    const { [pinnedFilterIdsKey]: _pinned, [filterOperatorsKey]: _operators, ...rest } = source;
    return rest as T;
}

// Change detection measures the blob the filter bar just wrote against the one a saved view was stored with, and the
// two come from different writers. The bar carries an operator even for a filter holding no value, and hands back a
// cleared range as a fully keyed { from: undefined } object; the saved view is server JSON that simply has no such
// key. Neither is a filter the user chose, so both sides are reduced to the entries that actually hold a value
// before they are compared. Without it, removing the last chip offers to reset a filter nobody changed.
//
// The pins deliberately survive, which is why this does not go through withoutFilterPresentationState: an empty chip
// parked in the bar is a filter the user put there, so a view carrying one differs from a view that does not and has
// something to reset. Stripping them is right for a query key, where a chip with no value changes no result.
export function comparableFilterData<T>(filterData: T | undefined): T | undefined {
    if (filterData === undefined || filterData === null) {
        return filterData;
    }

    const { [filterOperatorsKey]: _operators, ...rest } = filterData as Record<string, unknown>;
    return withoutEmptyEntries(rest) as T;
}

// Arrays and Dates are a filter's own value and are left whole. Only the objects that map a filter name to its value
// are pruned, at whatever depth they nest - selectFilters holds a second such map inside the first.
function withoutEmptyEntries(value: unknown): unknown {
    if (value === null || typeof value !== "object" || Array.isArray(value) || value instanceof Date) {
        return value;
    }

    const entries = Object.entries(value as Record<string, unknown>)
        .map(([key, entry]) => [key, withoutEmptyEntries(entry)] as const)
        .filter(([, entry]) => hasFilterValue(entry));

    return Object.fromEntries(entries);
}
